import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

const ROTATION_INTERVAL = 30000; // 30 seconds

export const useFeaturedRotation = (items, persistKey, idKey, setAction) => {
  const dispatch = useDispatch();
  const storedState = useSelector((state) => state.PERSIST?.[persistKey] || {});
  const storedStateRef = useRef(storedState);

  useEffect(() => {
    storedStateRef.current = storedState;
  }, [storedState]);

  const featuredItems = useMemo(() => {
    return (items || []).filter((e) => e.featured);
  }, [items]);

  const [currentFeatured, setCurrentFeatured] = useState(null);

  useEffect(() => {
    if (featuredItems.length === 0) {
      setCurrentFeatured(null);
      return;
    }

    if (featuredItems.length === 1) {
      setCurrentFeatured(featuredItems[0]);
      return;
    }

    let timeoutId;
    let isCancelled = false;
    
    const scheduleNextRotation = (currentId, delay) => {
      timeoutId = setTimeout(() => {
        if (isCancelled) return;
        
        const currentIndex = featuredItems.findIndex((e) => e.id === currentId);
        const nextIndex = currentIndex !== -1 ? (currentIndex + 1) % featuredItems.length : 0;
        const nextItem = featuredItems[nextIndex];
        
        const now = Date.now();
        setCurrentFeatured(nextItem);
        dispatch(setAction(nextItem.id, now + ROTATION_INTERVAL));
        
        scheduleNextRotation(nextItem.id, ROTATION_INTERVAL);
      }, delay);
    };

    const initializeRotation = () => {
      const { [idKey]: itemId, nextRotationAt } = storedStateRef.current || {};
      let initialItem = featuredItems.find(e => e.id === itemId);
      
      const now = Date.now();
      let timeRemaining = ROTATION_INTERVAL;
      let nextItemToSet = initialItem;

      if (!initialItem) {
        nextItemToSet = featuredItems[0];
      } else {
        if (nextRotationAt && nextRotationAt > now) {
          timeRemaining = nextRotationAt - now;
        } else {
          const elapsed = now - (nextRotationAt || now);
          const elapsedRotations = Math.floor(elapsed / ROTATION_INTERVAL) + 1;
          const currentIndex = featuredItems.findIndex(e => e.id === initialItem.id);
          const targetIndex = (currentIndex + elapsedRotations) % featuredItems.length;
          
          nextItemToSet = featuredItems[targetIndex];
          const remainingInCurrentSlice = ROTATION_INTERVAL - (elapsed % ROTATION_INTERVAL);
          timeRemaining = remainingInCurrentSlice;
        }
      }

      if (!isCancelled) {
        setCurrentFeatured(nextItemToSet);
        dispatch(setAction(nextItemToSet.id, now + timeRemaining));
        scheduleNextRotation(nextItemToSet.id, timeRemaining);
      }
    };

    initializeRotation();

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [featuredItems, dispatch, idKey, setAction]);

  const rotatedFeaturedItems = useMemo(() => {
    if (featuredItems.length === 0) return [];
    if (!currentFeatured) return featuredItems;
    
    const currentIndex = featuredItems.findIndex(e => e.id === currentFeatured.id);
    if (currentIndex === -1) return featuredItems;
    
    // Rotate array so current is first
    return [
      ...featuredItems.slice(currentIndex),
      ...featuredItems.slice(0, currentIndex)
    ];
  }, [featuredItems, currentFeatured]);

  const remainingItems = useMemo(() => {
    return (items || []).filter(e => !e.featured);
  }, [items]);

  return { currentFeatured, remainingItems, rotatedFeaturedItems };
};
